<template>
  <v-card dark class="show organization" v-if="organization">
    <v-card-title primary-title>
      <LogoUpload
        :src="organization.logo"
        :path="`/admin/organizations/${organization.id}/logo`"
        @success="logoSuccess"
      />
      <div>
        <div class="headline">{{ organization.name }}</div>
        <span class="subheading grey--text">{{ organization.slug }}</span>
      </div>
      <v-spacer />
      <v-tooltip left>
        <v-icon slot="activator" :color="organization.coverImage ? 'success' : 'warning'">
          mdi-file-image
        </v-icon>
        <span>Cover image {{ organization.coverImage ? 'uploaded' : 'missing' }}</span>
      </v-tooltip>
    </v-card-title>

    <v-divider />
    <v-card-text v-if="organization.summary">
      <div>Summary</div>
      <markdown :html="false" :source="organization.summary" class="markdown" />
    </v-card-text>
    <v-divider v-if="organization.summary" />
    <v-card-text v-if="organization.blurb">
      <div>Blurb</div>
      <p v-html="organization.blurb"></p>
    </v-card-text>
    <v-divider v-if="organization.blurb" />
    <v-card-text v-if="organization.culture">
      <div>Culture</div>
      <markdown :html="false" :source="organization.culture" class="markdown" />
    </v-card-text>
    <v-divider v-if="organization.culture" />

    <table class="v-table theme--dark">
      <tbody>
        <tr>
          <th class="text-xs-right">ID</th>
          <td>{{ organization.id }}</td>
        </tr>
        <tr>
          <th class="text-xs-right">Name</th>
          <td>{{ organization.name }}</td>
        </tr>
        <tr>
          <th class="text-xs-right">Slug</th>
          <td>{{ organization.slug }}</td>
        </tr>
        <tr>
          <th class="text-xs-right">Size</th>
          <td v-if="organization.size">{{ organization.size }} employees</td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Industries</th>
          <td v-if="organization.industries && organization.industries.length">
            <v-chip v-for="industry in organization.industries" :key="`industry-${industry}`">
              {{ industry }}
            </v-chip>
          </td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Website</th>
          <td v-if="organization.website">
            <a :href="organization.website" target="_blank">
              {{ organization.website }}
            </a>
          </td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Promotional Video</th>
          <td v-if="organization.video">
            <a :href="organization.video" target="_blank">
              {{ organization.video }}
            </a>
          </td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Subscriber Listing</th>
          <td v-if="organization.listing">
            <a
              :href="`https://listings.leap.ly/listings/${organization.listing}/respond`"
              target="_blank"
            >
              https://listings.leap.ly/listings/{{ organization.listing }}/respond
            </a>
          </td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Facebook</th>
          <td v-if="organization.facebook">
            <a :href="organization.facebook" target="_blank">
              {{ organization.facebook }}
            </a>
          </td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Twitter</th>
          <td v-if="organization.twitter">
            <a :href="organization.twitter" target="_blank">
              {{ organization.twitter }}
            </a>
          </td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">LinkedIn</th>
          <td v-if="organization.linkedin">
            <a :href="organization.linkedin" target="_blank">
              {{ organization.linkedin }}
            </a>
          </td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Instagram</th>
          <td v-if="organization.instagram">
            <a :href="organization.instagram" target="_blank">
              {{ organization.instagram }}
            </a>
          </td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr v-if="organization.location">
          <th class="text-xs-right">Headquarters</th>
          <td v-if="organization.location.id">
            {{ organization.location.address }}
          </td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Published</th>
          <td v-if="organization.publishedAt">
            {{ new Date(organization.publishedAt) | date('h:mm a, MMMM do yyyy') }}
            ({{ new Date(organization.publishedAt) | inWords({ addSuffix: true }) }})
          </td>
          <td v-else><em>Not specified</em></td>
        </tr>
        <tr></tr>
        <tr>
          <th class="text-xs-right">Created</th>
          <td>
            {{ new Date(organization.createdAt) | date('h:mm a, MMMM do yyyy') }}
            ({{ new Date(organization.createdAt) | inWords({ addSuffix: true }) }})
          </td>
        </tr>
        <tr>
          <th class="text-xs-right">Last updated</th>
          <td>
            {{ new Date(organization.updatedAt) | date('h:mm a, MMMM do yyyy') }}
            ({{ new Date(organization.updatedAt) | inWords({ addSuffix: true }) }})
          </td>
        </tr>
      </tbody>
    </table>

    <v-divider />

    <v-card-actions>
      <v-spacer />
      <can I="delete" a="Organization">
        <v-menu top max-width="30vw" v-model="deleting">
          <v-btn flat slot="activator" color="error">
            <v-icon left>mdi-delete</v-icon> Delete
          </v-btn>

          <v-card>
            <v-card-title class="headline">Are you sure?</v-card-title>
            <v-card-text>
              This action cannot be undone and will remove all associated data such as opportunities
              and applications. It is not recommended for any organization that has been active.
            </v-card-text>
            <v-card-actions>
              <v-btn flat block @click.native="deleting = false" color="grey">
                cancel
              </v-btn>
              <v-btn block @click="destroy" color="error">
                Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </can>

      <can I="edit" a="Organization">
        <v-btn @click="$emit('edit')" color="primary">
          <v-icon left>mdi-pencil</v-icon> Edit
        </v-btn>
      </can>
    </v-card-actions>
  </v-card>
</template>

<script>
// Components
import LogoUpload from '@/components/LogoUpload'
import Markdown from 'vue-markdown'

// Helpers
import { mapGetters } from 'vuex'

// Vue Component
export default {
  components: { LogoUpload, Markdown },
  data() {
    return {
      deleting: false,
    }
  },
  computed: mapGetters({ organization: 'admin/organizations/find' }),
  methods: {
    destroy() {
      this.$emit('destroy')
    },
    logoSuccess() {
      this.$store.commit('snackbar', {
        text: 'Logo uploaded!',
        type: 'success',
      })
      this.$store.dispatch('admin/organizations/fetch', this.organization.id)
    },
  },
}
</script>
